import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { SportsbookReportBase, SportsbookEntityType, SportsbookReportEntity } from '../../../../@types/api';
import { usePagination } from '../../../../hooks/usePagination';
import useSort from '../../../../hooks/useSort';
import { Box } from '@mui/material';
import TableTemplate from '../../../templates/TableTemplate';
import { getSportsbookColumns } from '../../../../config/sportsbookReports';
import { StyleObj } from '../../../../@types';
import { appendTotalsRow, generateDateRangeTimestamps, getTotalsRowClass } from '../../../../helpers';
import DateRangeSelect, { DayjsTimestamp } from '../../../molecules/DateRangeSelect';
import dayjs from 'dayjs';
import IconRenderer from '../../../atoms/IconRenderer';

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
};

const URL_TO_SPORTSBOOK_MODEL_TYPE_MAP: Record<string, SportsbookEntityType> = {
  tournaments: 'tournament',
  markets: 'market-type',
  sports: 'sport',
};

type SportsbookReportRow = SportsbookReportEntity & {
  isTotalsRow?: boolean;
};

const generateTotalsRow = (data?: SportsbookReportBase): SportsbookReportRow | undefined => {
  if (data) {
    return {
      id: 'totals',
      icon: 'Totals',
      mainWalletStakeAmount: data.mainWalletStakeAmount,
      ticketCount: data.ticketCount,
      stakeAmount: data.stakeAmount,
      winningAmount: data.winningAmount,
      profitLossAmount: data.profitLossAmount,
      profitMarginPercentage: data.profitMarginPercentage,
      isTotalsRow: true,
    };
  }
};

const getMobileCardHeaderItems = (report: SportsbookReportEntity) => [
  { value: <IconRenderer name={report.icon} small /> },
  { value: report.tournamentName || report.marketTypeName || report.sportName, flex: 1 },
  { label: 'Profit', value: report.profitLossAmount.toFixed(2) },
];

const SportsbookReportsEntityPage = () => {
  const location = useLocation();
  const { fromTimestamp, toTimestamp } = generateDateRangeTimestamps('today');
  const [date, setDate] = useState<DayjsTimestamp>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });
  const pathSegment = location.pathname.split('/').pop();
  const type = pathSegment && URL_TO_SPORTSBOOK_MODEL_TYPE_MAP[pathSegment];
  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<SportsbookReportEntity>(
    `reports/${type}`,
    {
      page: 1,
      limit: 25,
      fromTimestamp: fromTimestamp.valueOf(),
      toTimestamp: toTimestamp.valueOf(),
      walletType: 'main',
    }
  );

  const totalsRow = generateTotalsRow(data?.total);

  const aggregatedData = appendTotalsRow(data?.items, totalsRow);
  const { handleSort } = useSort(changeQuery);

  const { columns } = getSportsbookColumns(type as SportsbookEntityType);

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const handleDateChange = (newDate: DayjsTimestamp) => {
    setDate(newDate);
  };

  useEffect(() => {
    changeQuery({
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    });
  }, [date, changeQuery]);

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={aggregatedData}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={data?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        getRowClassName={getTotalsRowClass}
        changeQuery={changeQuery}
        mobileCardHeaderItems={getMobileCardHeaderItems}
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
        showExport
      />
    </Box>
  );
};

export default SportsbookReportsEntityPage;
